import { ErrorMessage } from "@atlaskit/form";
import { numberWithCommas } from "@fla/common";
import classNames from "classnames";
import { ReactNode } from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";

import { FormLabel } from "../FormLabel";
import "./range.css";

function addZeroes(num: number): string {
  return num.toLocaleString("en", {
    useGrouping: false,
    minimumFractionDigits: 1
  });
}

type RangeSliderProps<TFieldValues extends FieldValues, TContext extends object> = {
  title?: ReactNode;
  subTitle?: string;
  id: string;
  name?: FieldPath<TFieldValues>;
  control: Control<TFieldValues, TContext>;
  disabled?: boolean;
  readOnly?: boolean;
  onChange?: (value: string) => void;
  classes?: string;
  step?: number;
  marker?: number;
  exName?: string;
  percentageOf?: string;
  boxWidth?: string;
};

export function RangeSlider<TFieldValues extends FieldValues, TContext extends object>({
  title,
  subTitle,
  id,
  name = id as FieldPath<TFieldValues>,

  control,
  disabled = false,
  readOnly = false,
  onChange: _onChange,
  classes = "",
  step = 2.5,
  marker = 5,
  exName,
  percentageOf,
  boxWidth = "2/5"
}: RangeSliderProps<TFieldValues, TContext>) {
  return (
    <article id={name} className={classes}>
      <div className="mb-2">
        {title ? (
          <FormLabel htmlFor={name} id={name}>
            {title}
          </FormLabel>
        ) : null}
        {subTitle ? <p className="text-sm font-light text-gray-600">{subTitle}</p> : null}
      </div>

      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          return (
            <section className="relative w-full pt-10">
              <section className="w-full flex justify-between items-center absolute z-0">
                {Array(marker)
                  .fill({})
                  .map((_, idx) => (
                    <div key={idx} className="flex flex-col relative">
                      <div className="absolute -mt-8 -ml-2 text-xs text-gray-500">
                        {((100 / (marker - 1 > 0 ? marker - 1 : 0)) * idx).toFixed(0)}%
                      </div>
                      <span
                        className={classNames("w-0.5 h-5 bg-[#666666] rounded-lg", {
                          "w-1 h-8": idx === 0 || idx === marker - 1
                        })}
                      />
                    </div>
                  ))}
              </section>
              <input
                step={step}
                type="range"
                min={0}
                max={100}
                className={classNames("z-10", {
                  slider: value,
                  "disable-slider": !value
                })}
                style={{ position: "inherit" }}
                id="myRange"
                value={value}
                onChange={ev => {
                  const val = ev.target.value;
                  onChange(val);
                  if (typeof _onChange === "function") _onChange(val);
                }}
                onBlur={onBlur}
                disabled={disabled}
                readOnly={readOnly}
              />

              <div className="w-full flex justify-between items-center text-xs my-8">
                <div className={`w-${boxWidth}`}>
                  <div className="rounded-xl w-full py-10 bg-[#f2f2f2] bg-opacity-70 flex flex-col justify-center items-center px-4">
                    <p className="text-primary text-lg font-bold">{addZeroes(Number(value || 0))}%</p>
                    <p className="text-sm font-bold">You</p>
                    {percentageOf ? (
                      <div className="w-full text-gray-600 border-t border-gray-300 mt-4 pt-4 text-center space-y-1">
                        <p>approx</p>
                        <p className="text-base font-bold">${numberWithCommas((Number(percentageOf) * value) / 100)}</p>
                        <p>of balance</p>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className={`w-${boxWidth}`}>
                  <div className="rounded-xl w-full py-10 bg-[#f2f2f2] bg-opacity-70 flex flex-col justify-center items-center px-4">
                    <p className="text-primary text-lg font-bold">{addZeroes(100 - value)}%</p>
                    <p className="text-sm font-bold">{exName}</p>
                    {percentageOf ? (
                      <div className="w-full text-gray-600 border-t border-gray-300 mt-4 pt-4 text-center space-y-1">
                        <p>approx</p>
                        <p className="text-base font-bold">
                          ${numberWithCommas((Number(percentageOf) * (100 - value)) / 100)}
                        </p>
                        <p>of balance</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              {error ? <ErrorMessage>{error.message}</ErrorMessage> : null}
            </section>
          );
        }}
      />
    </article>
  );
}
